import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, FormGroup, Label, Input, Form, InputGroup, InputGroupText } from 'reactstrap';
import PagosPlanillaService from '../../axios_services/pagosplanilla.service';
import ArchivosService from '../../axios_services/archivos.service';
import Loader from '../Loader/Loader';
import { AppContext } from '../../application/provider';

const formatSoles = (amount) =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(amount || 0);

const ModalRegistroPago = ({ isOpen, onClose, onRefresh, item, periodo }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);

    // Bonos y descuento editables
    const [aplicaBonoAnual, setAplicaBonoAnual] = useState('0');
    const [aplicaBonoGestion, setAplicaBonoGestion] = useState('0');
    const [descuentoActual, setDescuentoActual] = useState(0);

    // Totales recalculados
    const [totalPagar, setTotalPagar] = useState(0);
    const [totalAporte, setTotalAporte] = useState(0);
    const [importeNeto, setImporteNeto] = useState(0);
    const [bonoAnualEfec, setBonoAnualEfec] = useState(0);
    const [bonoGestEfec, setBonoGestEfec] = useState(0);
    const [liquidacion, setLiquidacion] = useState(0);
    const [retencion5ta, setRetencion5ta] = useState(0);

    // Campos de pago
    const [tipo_pagos] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 32));
    const [fechaPago, setFechaPago] = useState('');
    const [pagoId, setPagoId] = useState('');
    const [boletaFile, setBoletaFile] = useState(null);
    const fileRef = useRef(null);

    const userProfile = Number(state.user?.perfil_id);
    const canUseActions = [5, 8, 22, 26].includes(userProfile);
    const canUploadBoleta = [5, 8, 22, 26].includes(userProfile);
    const isLocked = !canUseActions || (item && Number(item.validacion_id) > 0);

    useEffect(() => {
        if (isOpen && item) {
            setAplicaBonoAnual(String(item.aplica_bono_anual ?? 0));
            setAplicaBonoGestion(String(item.aplica_bono_gestion ?? 0));
            setDescuentoActual(item.descuento || 0);
            setLiquidacion(item.liquidacion || 0);
            setRetencion5ta(item.retencion_5ta > 0 ? parseFloat(item.retencion_5ta).toFixed(2) : '0.00');
            setFechaPago(item.fecha_pago || '');
            setPagoId(item.pago_id || '');
            setBoletaFile(null);
            if (fileRef.current) fileRef.current.value = '';
        }
    }, [isOpen, item]);

    useEffect(() => {
        if (!item) return;

        const bAnual = aplicaBonoAnual === '1' ? (item.bono_anual || 0) : 0;
        const bGestion = aplicaBonoGestion === '1' ? (item.bono_gestion || 0) : 0;

        setBonoAnualEfec(bAnual);
        setBonoGestEfec(bGestion);

        const rem = item.remuneracion || 0;
        const desc = parseFloat(descuentoActual || 0);
        const liq = parseFloat(liquidacion || 0);
        const totalCalc = parseFloat(((rem + bAnual + bGestion + liq) - desc).toFixed(2));

        let aporte = 0;
        if (item.tipo_aportacion == 1) { // ONP
            aporte = parseFloat((totalCalc * 0.13).toFixed(2));
        } else if (item.tipo_aportacion == 2) { // AFP
            let comision = 0;
            if (item.categoria_aportacion == 4 && item.subcategoria_aportacion == 1) {
                comision = 0.0155;
            }
            aporte = parseFloat((totalCalc * (0.10 + comision + 0.0137)).toFixed(2));
        }

        const ret5ta = parseFloat(retencion5ta || 0);
        const netoCalc = parseFloat((totalCalc - aporte - ret5ta).toFixed(2));

        setTotalPagar(totalCalc);
        setTotalAporte(aporte);
        setImporteNeto(netoCalc);
    }, [aplicaBonoAnual, aplicaBonoGestion, descuentoActual, liquidacion, retencion5ta, item]);

    if (!item) return null;

    const handleRegistrar = async () => {
        setIsLoading(true);
        try {
            if (!isLocked) {
                await PagosPlanillaService.registrarPago({
                    usuario_id: item.usuario_id,
                    periodo: periodo,
                    remuneracion: item.remuneracion || 0,
                    aplica_bono_anual: parseInt(aplicaBonoAnual),
                    bono_anual: bonoAnualEfec,
                    aplica_bono_gestion: parseInt(aplicaBonoGestion),
                    bono_gestion: bonoGestEfec,
                    liquidacion: parseFloat(liquidacion || 0),
                    descuento: parseFloat(descuentoActual || 0),
                    total_pagar_planilla: totalPagar,
                    total_aporte_trabajador: totalAporte,
                    retencion_5ta: parseFloat(retencion5ta || 0),
                    importe_neto: importeNeto,
                });
            }

            if (canUploadBoleta || !isLocked) {
                await PagosPlanillaService.actualizarPago({
                    usuario_id: item.usuario_id,
                    periodo: periodo,
                    fecha_pago: fechaPago || null,
                    pago_id: pagoId || null,
                });
            }

            if (boletaFile && canUploadBoleta) {
                try {
                    await ArchivosService.guardarBoletaPago(boletaFile, item.usuario_id, periodo, state.user.usuario_id);
                } catch (uploadError) {
                    const errorMsg = uploadError.response?.data?.message || '';
                    if (errorMsg.includes('failed to upload')) {
                        throw new Error('El archivo PDF pesa demasiado. Por favor, reduce el tamaño del PDF (límite del servidor típicamente 2MB) o verifica tu archivo.');
                    }
                    throw uploadError;
                }
            }

            alert('Datos actualizados correctamente.');
            if (onRefresh) onRefresh();
            onClose();
        } catch (e) {
            console.error(e);
            alert(e.message || 'Error al actualizar el registro. Inténtelo nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="lg" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>ACTUALIZAR REGISTRO PLANILLA</h1>
                    <button className="close-btn" onClick={onClose}>X</button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {isLoading && <Loader />}

                    <Form onSubmit={(e) => { e.preventDefault(); handleRegistrar(); }} className="px-4 mt-2">

                        <FormGroup className="mb-3">
                            <Label>Colaborador:</Label>
                            <Input type="text" className="form-control" value={item.nombre_completo || ''} disabled />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Remuneración:</Label>
                            <Input type="text" className="form-control" value={formatSoles(item.remuneracion)} disabled />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Label className="m-0">Descuento:</Label>
                                {item.descuento_calculado !== undefined && parseFloat(item.descuento_calculado) !== parseFloat(descuentoActual) && (
                                    <small className="text-primary font-semibold text-[11px]">
                                        Tardanzas/Asistencias actuales: {formatSoles(item.descuento_calculado)}
                                    </small>
                                )}
                            </div>
                            <Input type="text" className="form-control" value={formatSoles(descuentoActual)} disabled />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Bono Anual:</Label>
                            <Input type="select" className="form-control" value={aplicaBonoAnual} onChange={(e) => setAplicaBonoAnual(e.target.value)} disabled={isLocked}>
                                <option value="1">{formatSoles(item.bono_anual)}</option>
                                <option value="0">S/ 0.00</option>
                            </Input>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Bono por Gestión:</Label>
                            <Input type="select" className="form-control" value={aplicaBonoGestion} onChange={(e) => setAplicaBonoGestion(e.target.value)} disabled={isLocked}>
                                <option value="1">{formatSoles(item.bono_gestion)}</option>
                                <option value="0">S/ 0.00</option>
                            </Input>
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Retención 5ta Categoría:</Label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#495057', fontWeight: '400', fontSize: '11.5px', pointerEvents: 'none' }}>
                                    S/
                                </span>
                                <Input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    style={{ paddingLeft: '30px' }}
                                    value={retencion5ta}
                                    onChange={(e) => setRetencion5ta(e.target.value)}
                                    onBlur={(e) => setRetencion5ta(parseFloat(e.target.value || 0).toFixed(2))}
                                    disabled={isLocked}
                                    placeholder="0.00"
                                />
                            </div>
                        </FormGroup>

                        {item.fecha_cese && (
                            <FormGroup className="mb-3">
                                <Label className="text-success fw-bold">Liquidación Vacaciones Truncas:</Label>
                                <Input
                                    type="number"
                                    className="form-control border-success"
                                    style={{ borderRadius: '25px' }}
                                    value={liquidacion}
                                    onChange={(e) => setLiquidacion(e.target.value)}
                                    disabled={isLocked}
                                    placeholder="0.00"
                                    step="0.01"
                                />
                            </FormGroup>
                        )}

                        <FormGroup className="mb-3">
                            <Label>Total a Pagar Planilla:</Label>
                            <Input type="text" className="form-control fw-bold text-primary" value={formatSoles(totalPagar)} disabled />
                        </FormGroup>

                        <FormGroup className="mb-3">
                            <Label>Total Aporte Trabajador:</Label>
                            <Input type="text" className="form-control text-[12px]" value={item.tipo_aportacion ? formatSoles(totalAporte) : '⚠️ FALTA ASIGNAR'} disabled />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label>Importe Neto a Pagar:</Label>
                            <Input type="text" className="form-control fw-bold text-success fs-5" value={formatSoles(importeNeto)} disabled />
                        </FormGroup>

                        {isLocked && (
                            <>
                                {canUploadBoleta && (
                                    <FormGroup className="mb-4">
                                        <Label>Subir/Actualizar Boleta de Pago (PDF):</Label>
                                        <div className="cap-upload-zone">
                                            <span className="cap-upload-btn" onClick={() => fileRef.current?.click()}>
                                                {boletaFile ? boletaFile.name : 'Seleccionar archivo (PDF)'}
                                            </span>
                                            <input
                                                ref={fileRef}
                                                type="file"
                                                accept=".pdf"
                                                style={{ display: 'none' }}
                                                onChange={(e) => setBoletaFile(e.target.files[0] || null)}
                                            />
                                        </div>
                                        {boletaFile && (
                                            <small>
                                                <span style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }} onClick={() => { setBoletaFile(null); if (fileRef.current) fileRef.current.value = ''; }}>
                                                    ✕ Quitar selección
                                                </span>
                                            </small>
                                        )}
                                        {item.boleta_url && !boletaFile && (
                                            <small className="text-success mt-1 d-block">
                                                ✓ Ya existe una boleta subida.{' '}
                                                <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(item.boleta_url, '_blank')}>Ver documento</span>
                                            </small>
                                        )}
                                    </FormGroup>
                                )}
                            </>
                        )}

                        <div className="flex justify-between items-center mt-4 pb-3">
                            {!isLocked && (
                                <Button
                                    type="button"
                                    color="info"
                                    outline
                                    className="px-3 py-2 text-xs font-semibold"
                                    style={{ borderRadius: '8px' }}
                                    onClick={() => {
                                        const nuevoDesc = item.descuento_calculado !== undefined ? item.descuento_calculado : (item.descuento || 0);
                                        setDescuentoActual(nuevoDesc);
                                    }}
                                >
                                    🔄 Actualizar Descuentos ({formatSoles(item.descuento_calculado ?? item.descuento ?? 0)})
                                </Button>
                            )}
                            <Button className="btn-primary px-5 py-2 ms-auto" onClick={handleRegistrar} style={{ borderRadius: '8px' }} disabled={isLoading}>
                                GUARDAR CAMBIOS
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalRegistroPago;